import { Component } from "react";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";

/**
 * Global Error Boundary — catches render-time exceptions thrown anywhere in
 * the React tree and renders a branded "Something went wrong" screen with a
 * Reload button instead of the bare `#0A0A0A` background users were seeing.
 *
 * Built as a class component because React still only supports the error-
 * boundary lifecycle methods (`getDerivedStateFromError` /`componentDidCatch`)
 * on classes.
 *
 * Note: i18n is intentionally NOT used here. If the React tree crashed,
 * the I18nProvider context may not be reachable either; we fall back to a
 * minimal trilingual copy so the user always sees something readable.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info?.componentStack);
    // Best-effort beacon to the backend so we can monitor production crashes
    // without setting up a third-party telemetry tool. Same-origin cookie auth.
    // without setting up a third-party telemetry tool. Same-origin cookie auth.
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL || ""}/api/client-errors`;
      const blob = new Blob([JSON.stringify({
        message: String(error?.message || error),
        stack: String(error?.stack || ""),
        component_stack: String(info?.componentStack || ""),
        url: typeof window !== "undefined" ? window.location.href : "",
        ua: typeof navigator !== "undefined" ? navigator.userAgent : "",
        ts: new Date().toISOString(),
      })], { type: "application/json" });
      if (navigator.sendBeacon) navigator.sendBeacon(url, blob);
    } catch (_) { /* swallow */ }
  }

  reload = () => {
    try { window.location.reload(); } catch (_) { /* noop */ }
  };

  home = () => {
    try { window.location.assign("/"); } catch (_) { /* noop */ }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const lang = (typeof window !== "undefined" && localStorage.getItem("ks_lang")) || "en";
    const COPY = {
      es: {
        title: "Algo ha fallado",
        body: "Hemos detectado un error inesperado. Tus datos están seguros — solo necesitamos recargar la pantalla.",
        reload: "Recargar",
        home: "Ir al inicio",
        hint: "Si el problema continúa, escríbenos a support@keepsoul.app",
      },
      en: {
        title: "Something went wrong",
        body: "We hit an unexpected error. Your data is safe — we just need to reload the screen.",
        reload: "Reload",
        home: "Go home",
        hint: "If the problem persists, email support@keepsoul.app",
      },
      zh: {
        title: "出了点问题",
        body: "我们遇到了意外错误。您的数据是安全的 — 我们只需重新加载页面。",
        reload: "重新加载",
        home: "返回首页",
        hint: "如果问题持续，请联系 support@keepsoul.app",
      },
      ko: {
        title: "문제가 발생했습니다",
        body: "예기치 않은 오류가 발생했습니다. 데이터는 안전합니다 — 화면만 새로고침하면 됩니다.",
        reload: "새로고침",
        home: "홈으로",
        hint: "문제가 계속되면 support@keepsoul.app로 문의해 주세요",
      },
    };
    const t = COPY[lang] || COPY.en;

    return (
      <div
        className="min-h-screen flex items-center justify-center px-6"
        style={{ background: "#0A0A0A", color: "#F2EDE4", fontFamily: "Manrope, system-ui, sans-serif" }}
        data-testid="error-boundary"
      >
        <div
          className="max-w-md w-full text-center p-8 sm:p-10 rounded-md"
          style={{ background: "#1E1D1B", border: "1px solid rgba(201,163,106,0.25)" }}
        >
          <div className="flex justify-center mb-5">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center"
              style={{ background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.35)" }}
            >
              <AlertTriangle className="w-7 h-7" style={{ color: "#F87171" }} strokeWidth={1.4} />
            </div>
          </div>

          <h1
            className="text-3xl mb-3"
            style={{ color: "#E8C997", fontFamily: "Cormorant Garamond, Georgia, serif", fontWeight: 300 }}
            data-testid="error-boundary-title"
          >
            {t.title}
          </h1>
          <p style={{ color: "#A39B8E", fontSize: 14, lineHeight: 1.6, marginBottom: 28 }}>
            {t.body}
          </p>

          <div className="flex flex-col gap-2">
            <button
              onClick={this.reload}
              data-testid="error-boundary-reload"
              style={{
                background: "#C9A36A",
                color: "#0A0A0A",
                padding: "12px 24px",
                borderRadius: 4,
                fontWeight: 600,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                border: "none",
                cursor: "pointer",
                fontSize: 15,
              }}
            >
              <RefreshCw className="w-4 h-4" /> {t.reload}
            </button>
            <button
              onClick={this.home}
              data-testid="error-boundary-home"
              style={{
                background: "transparent",
                color: "#B5ACA0",
                padding: "10px 24px",
                borderRadius: 4,
                border: "1px solid rgba(201,163,106,0.3)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                cursor: "pointer",
                fontSize: 14,
              }}
            >
              <Home className="w-4 h-4" /> {t.home}
            </button>
          </div>

          {process.env.NODE_ENV === "development" && this.state.error && (
            <pre
              style={{
                marginTop: 24,
                textAlign: "left",
                fontSize: 11,
                color: "#F87171",
                background: "rgba(0,0,0,0.4)",
                padding: 12,
                borderRadius: 4,
                overflow: "auto",
                maxHeight: 200,
              }}
            >
              {String(this.state.error?.stack || this.state.error)}
            </pre>
          )}

          <p style={{ color: "#71717A", fontSize: 11, marginTop: 24 }}>{t.hint}</p>
        </div>
      </div>
    );
  }
}
